import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import { Dispatch } from 'redux';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import toastHandler from 'Utils/toastHandler';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';
import ObrasApi from 'Data/Obras.api';
import { IArchivedStatusRequest } from 'Data/interfaces/Obra/IArchivedStatusRequest';
import { NavigateFunction } from 'react-router';
import { IUserLimitsActive } from 'Store/Auth/interfaces/IAuthState';
import { AuthActions } from 'Store/Auth/Auth.actions';

interface IUseQueryUpdateArchivedObraProps {
  navigate: NavigateFunction;
  dispatch: Dispatch;
}

export const useQueryUpdateArchivedObra = ({
  navigate,
  dispatch,
}: IUseQueryUpdateArchivedObraProps) => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IArchivedStatusRequest) => {
      return ObrasApi.updateArchivedStatus(variables);
    },
    onMutate: async (variables) => {
      const previousObras = queryClient.getQueryData<IObraResponse[]>(
        [OBRA_QUERY_KEY],
      );
      const previousUniqueObra = queryClient.getQueryData<IObraResponse>(
        [OBRA_QUERY_KEY, Number(variables.ConstructionSiteId)],
      );

      queryClient.setQueryData<IObraResponse[]>([OBRA_QUERY_KEY], (
        oldData,
      ) => {
        return oldData?.map((obra) => {
          if (obra.ConstructionSiteId === variables.ConstructionSiteId) {
            return { ...obra, isArchived: variables.isArchived };
          }
          return obra;
        });
      });
      queryClient.setQueryData<IObraResponse>([OBRA_QUERY_KEY, Number(variables.ConstructionSiteId)], (
        oldData,
      ) => {
        if (oldData && oldData.ConstructionSiteId === variables.ConstructionSiteId) {
          return { ...oldData, isArchived: variables.isArchived };
        }
        return oldData;
      });

      let userLimitsActive: IUserLimitsActive;
      if (variables.isArchived) {
        userLimitsActive = {
          QtyActiveConstructionSites: -1,
        };
      } else {
        userLimitsActive = {
          QtyActiveConstructionSites: 1,
        };
      }
      dispatch(AuthActions.toogleUserLimitsActive(userLimitsActive));

      navigate(`/obras`);
      return { previousObras, previousUniqueObra };
    },
    onSuccess: () => {
      toastHandler.showSuccess('Obra arquivada/desarquivada com sucesso');
    },
    onError: async (error: any, variables, context) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] }),
        queryClient.cancelQueries({
          queryKey: [OBRA_QUERY_KEY, Number(variables.ConstructionSiteId)],
        }),
      ]);

      queryClient.setQueryData([OBRA_QUERY_KEY], context?.previousObras);
      queryClient.setQueryData(
        [OBRA_QUERY_KEY, Number(variables.ConstructionSiteId)],
        context?.previousUniqueObra,
      );

      const errorMsg = error.errors
        ? error.errors[0].Message
        : 'Erro ao arquivar/desarquivar obra!';
      toastHandler.showError(errorMsg);
    },
  });

  return {
    updateArchivedObra: mutate,
    isLoading: isPending,
  };
};
